import { useEffect, useRef, useState } from 'react';
import { CategoryList, clientSession, Content, fetchSystemConfigurations, fetchVideoContents,  ResponseContainer } from 'common/apiService';
import ContentFilter, { stateToQueryString } from 'common/ContentFilter';
import Layout from 'common/Layout/Layout';
import { Text } from 'common/styles';
import ContentList from 'common/ContentList';
import Head from 'next/head';
import InfiniteScroll from 'common/InfiniteScroll';
import usePagination from 'common/usePagination';
import { __t } from 'common/i18n';
import ContentListLoading from 'common/ContentListLoading';
import { useRouter } from 'next/router';
import xIPCountry from 'common/apiService/xIPCountry';

interface ContentsPageProps {
  ssrData: ResponseContainer<Content[]>;
  ssrUrl: string;
  category_filters: CategoryList[];
}

export default function ContentsPage({ ssrData, ssrUrl, category_filters }: ContentsPageProps) {
  const router = useRouter();
  const { query } = router;

  const [state, setState] = useState<any>({
    initialData: ssrData,
    initialUrl: ssrUrl,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const queryString = stateToQueryString(query);
  const queryRef = useRef(queryString);

  useEffect(() => {
    if (queryRef.current !== queryString) {
      queryRef.current = queryString;
      setLoading(true);
      fetchVideoContents(query, clientSession).then(({ data, meta }) => {
        setLoading(false);
        setState({
          initialData: { data, meta },
          initialUrl: queryString,
        });
      }).catch(() => {
        setLoading(false);
      });
    }
  }, [query, queryString]);

  const { data: contents, getInfiniteScrollProps, hasMore } = usePagination<Content>(state);

  return (
    <Layout disableFooter={hasMore}>
      <Head>
        <title>{__t('siteName')} | {__t('common.search')}</title>
      </Head>

      <ContentFilter loading={loading} category_filters={category_filters} />

      <section id="filter-results">
        {contents?.length > 0
          ? (
            <InfiniteScroll {...getInfiniteScrollProps()} loader={<ContentListLoading />}>
              <ContentList items={contents} />
            </InfiniteScroll>
          ) : (loading
            ? <ContentListLoading />
            : <Text color="error">{__t('errors.noresult')}</Text>
          )
        }
      </section>
    </Layout>
  );
}

export async function getServerSideProps({ query, resolvedUrl, req, res }) {
  xIPCountry({ req, res });

  const { data: { category_filters } } = await fetchSystemConfigurations();
  const { data, meta } = await fetchVideoContents(query);
  const ssrUrl = resolvedUrl.replace('/contents', '/video-contents');

  return {
    props: {
      ssrData: { data, meta },
      ssrUrl,
      category_filters,
    },
  };
}
